/**
 * Types for the Ask API async request/response contract
 */

// ─── Invoke API Types ───────────────────────────────────────────────────────

export interface InvokeTextRequest {
  instructions?: string
  messages: string[]
  model?: string
  responseSchema?: string
  /** Optional conversation ID for stateful conversations. Pass null for stateless calls. */
  conversationId?: string | null
}

export interface InvokeTextResponse {
  output: string
  /** The conversation ID for continuing the conversation. */
  conversationId?: string | null
}

export interface InvokeImageRequest {
  instructions?: string
  prompt: string
  /** Base64-encoded image data */
  image: string
  /** Image format (e.g., "png", "jpeg"). Defaults to "png". */
  imageType?: string
  model?: string
  responseSchema?: string
  /** Optional conversation ID for stateful conversations. Pass null for stateless calls. */
  conversationId?: string | null
}

export interface InvokeImageResponse {
  output: string
  /** The conversation ID for continuing the conversation. */
  conversationId?: string | null
}

export interface CreateConversationResponse {
  /** The conversation ID to use in subsequent invoke requests. */
  conversationId: string
}

// ─── Ask API Types ──────────────────────────────────────────────────────────

export interface AskRequest {
  prompt: string
  responseFormat: 'json' | 'text'
  responseSchema?: string
  workItemId?: string | null
}

export interface AskAcceptedResponse {
  status: 'accepted'
  operationId: string
  workItemId?: string | null
}

export interface AskCompletedResponse {
  status: 'completed'
  output: string
  responseFormat?: 'json' | 'text' | null
  workItemId?: string | null
}

export type AskPostResponse = AskAcceptedResponse | AskCompletedResponse
export type AskOperationResponse = AskAcceptedResponse | AskCompletedResponse

// Raw work item from LLM output — all fields may be empty strings
export interface RawWorkItem {
  id: string
  title: string
  severity: string
  category: string
  dueDate: string | null
  createdDate: string
}

export interface RawWorkListResponse {
  workItems: RawWorkItem[]
}

// Raw work item details from LLM output — all optional fields may be null
export interface RawInsightContentItem {
  id: string
  type: string
  content: string
  href?: string | null
  mentionId?: string | null
  mentionName?: string | null
  buttonVariant?: string | null
  tooltipContent?: string | null
  checked?: boolean | null
  stepStatus?: string | null
  stepNumber?: number | null
}

export interface RawBlockerInfo {
  description: string
  duration: string
}

export interface RawUnblockContact {
  id: string
  displayName: string
  role?: string | null
}

export interface RawFixGuidanceLink {
  id: string
  label: string
  url: string
  type: string
}

export interface RawTsgDocument {
  id: string
  title: string
  url: string
  content?: string | null
  type: string
}

export interface RawVideoInfo {
  id: string
  title: string
  url: string
  thumbnailUrl?: string | null
  duration: string
  type: string
  summary?: string | null
}

export interface RawAutomationScript {
  id: string
  title: string
  content: string
  canLaunch: boolean
  launchDisabledReason?: string | null
}

export interface RawHistoryEntry {
  id: string
  timestamp: string
  type: string
  actorId: string
  actorDisplayName: string
  description: string
  details?: string | null
}

export interface RawConfidenceSignal {
  label: string
  isPositive: boolean
}

export interface RawWorkItemDetailsResponse {
  id: string
  title: string
  description: string
  severity: string
  category: string
  dueDate?: string | null
  createdDate: string
  updatedDate: string
  owner?: { id: string; displayName: string; email?: string | null } | null
  recentUpdates?: RawInsightContentItem[] | null
  nextSteps?: RawInsightContentItem[] | null
  blockers?: RawBlockerInfo[] | null
  unblockers?: RawUnblockContact[] | null
  fixGuidanceLinks?: RawFixGuidanceLink[] | null
  tsgDocuments?: RawTsgDocument[] | null
  videos?: RawVideoInfo[] | null
  automationScript?: RawAutomationScript | null
  history?: RawHistoryEntry[] | null
  effortEstimate?: string | null
  aiConfidence?: string | null
  confidenceSignals?: RawConfidenceSignal[] | null
  blockedDuration?: string | null
  sourceSystem?: string | null
  externalId?: string | null
  relatedIssueIds?: string[] | null
  // Appended by API post-LLM, not from schema
  tsgResearch?: string | null
}

// Type guards
export function isAskAccepted(
  response: AskPostResponse | AskOperationResponse
): response is AskAcceptedResponse {
  return response.status === 'accepted'
}

export function isAskCompleted(
  response: AskPostResponse | AskOperationResponse
): response is AskCompletedResponse {
  return response.status === 'completed'
}
